import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play, Radio, ArrowUpRight } from "lucide-react";

export default function Hero({ onWatch }) {
  const [featured, setFeatured] = useState(null);
  const [hasLive, setHasLive] = useState(false);

  useEffect(() => {
    api.get("/youtube/videos").then((r) => {
      const vids = r.data.videos || [];
      const live = vids.find((v) => v.is_live);
      setHasLive(!!live);
      setFeatured(live || vids[0] || null);
    }).catch(() => {});
  }, []);

  return (
    <section className="px-6 md:px-12 lg:px-16 pt-12 pb-20" data-testid="hero-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 flex flex-col justify-between">
          <div className="flex items-center gap-3 text-halo-red font-sports uppercase tracking-[0.3em] text-xs">
            <span className="w-2 h-2 bg-halo-red rounded-full animate-pulse-live" />
            <span>Independent Angels Podcast Network</span>
          </div>
          <h1 className="font-sports uppercase tracking-tight leading-[0.85] text-[18vw] sm:text-[14vw] lg:text-[9.5vw] mt-6 text-balance text-halo-navy">
            Halos
            <br />
            <span className="text-halo-red">in the</span>
            <br />
            Infield.
          </h1>
          <p className="mt-8 max-w-xl font-body text-base sm:text-lg text-halo-navy/70 leading-relaxed">
            Postgame reactions, deep-dive analysis and unfiltered fan takes — every Angels game, every week.
            New episodes drop on YouTube, with the loudest section in baseball.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid="hero-watch-btn"
              href={featured?.url || "https://www.youtube.com/@halosintheinfield"}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-halo-red hover:bg-halo-redHover text-white font-sports uppercase tracking-wider px-7 py-4 text-sm"
            >
              <Play className="w-4 h-4 fill-white" strokeWidth={3} />
              {hasLive ? "Watch Live" : "Latest Episode"}
            </a>
            <a
              data-testid="hero-shop-btn"
              href="/shop"
              className="inline-flex items-center gap-2 border-2 border-halo-navy text-halo-navy hover:bg-halo-navy hover:text-halo-cream font-sports uppercase tracking-wider px-7 py-4 text-sm transition-colors"
            >
              Shop Merch <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-halo-navy text-halo-cream p-6 h-full flex flex-col gap-4 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1629259379391-a19d74aaba4b?w=1200&q=80')] bg-cover bg-center" />
            <div className="relative z-10 flex items-center gap-2 font-sports uppercase tracking-[0.25em] text-xs">
              {hasLive ? (
                <>
                  <Radio className="w-4 h-4 text-halo-red animate-pulse-live" />
                  <span className="text-halo-red">Live On Air</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-halo-red" />
                  <span>Featured Episode</span>
                </>
              )}
            </div>
            <div className="relative z-10 flex-1 mt-4">
              {featured ? (
                <a href={featured.url} target="_blank" rel="noreferrer" data-testid="hero-featured-video" className="block group">
                  <div className="aspect-video overflow-hidden border border-halo-navyLight">
                    <img
                      src={featured.thumbnail}
                      alt={featured.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1589903308904-1010c2294adc?w=800&q=80"; }}
                    />
                  </div>
                  <h3 className="mt-4 font-sports uppercase tracking-tight text-2xl leading-tight">
                    {featured.title}
                  </h3>
                </a>
              ) : (
                <div className="aspect-video bg-halo-navyLight flex items-center justify-center border border-halo-navyLight">
                  <Radio className="w-12 h-12 text-halo-red" />
                </div>
              )}
            </div>
            <div className="relative z-10 mt-2 flex items-center justify-between text-xs font-sports uppercase tracking-widest text-halo-cream/70">
              <span>HITI Podcast</span>
              <span>·</span>
              <span>YouTube</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
